import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, Input, InputSignal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { StringSignal } from '@studiohyperdrive/ngx-utils';

import { CypressTagDirective } from '@vlaio/cypress/core';
import { CompanyAddressRecord } from '@vlaio/shared/company';
import { HasFeatureDirective } from '@vlaio/shared/features';
import { ELoketRoutePaths } from '@vlaio/shared/route-paths';
import { MediaQueryMax } from '@vlaio/shared/types';
import {
	VlaioValueTileComponent,
	VlaioAddressComponent,
	VlaioDetailContentComponent,
	VlaioPartnerDetailComponent,
	VlaioIconComponent,
	VlaioContactItemsMapPipe,
	VlaioInternalButtonComponent,
	VlaioExternalButtonComponent
} from '@vlaio/shared/ui/common';
import { MediaQueryDirective } from '@vlaio/shared/ui/device';
import { UserEntity } from '@vlaio/shared/user';

import { PermitsEntity } from '../../../data';
import { I18nKeys } from '../../../i18n';

@Component({
	selector: 'permits-detail',
	templateUrl: './permits-detail.component.html',
	styleUrls: ['./permits-detail.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CypressTagDirective,
		VlaioIconComponent,
		VlaioValueTileComponent,
		VlaioAddressComponent,
		VlaioInternalButtonComponent,
		VlaioExternalButtonComponent,
		HasFeatureDirective,
		DatePipe,
		TranslateModule,
		VlaioDetailContentComponent,
		VlaioPartnerDetailComponent,
		MediaQueryDirective,
		VlaioContactItemsMapPipe
	]
})
export class PermitsDetailComponent {
	/**
	 * The translation keys.
	 */
	public readonly i18nKeys: typeof I18nKeys = I18nKeys;

	/**
	 * The maximum media query.
	 */
	public readonly mediaQueryMax: typeof MediaQueryMax = MediaQueryMax;

	/**
	 * The available route paths.
	 */
	public readonly routePaths: typeof ELoketRoutePaths = ELoketRoutePaths;

	/**
	 * The permit to render in the detail view.
	 */
	public readonly permit: InputSignal<PermitsEntity> = input.required();

	/**
	 * Parse the `fileNumber` to an explicit string to be navigable to the `Cases` page.
	 */
	public readonly fileSearchQuery: StringSignal = computed(() => {
		// Wouter: Wrap the fileNumber in quotes to make sure the search works correctly.
		return `"${this.permit().status?.fileNumber}"` || '';
	});

	/**
	 * The permit to render in the detail view.
	 */
	@Input({ required: false }) public company: UserEntity;
	/**
	 * A record of the branchNumber and its address.
	 */
	@Input({ required: true }) public addresses: CompanyAddressRecord;

	/**
	 * Whether or not the table is read-only, by default false. Setting this to true will hide the buttons
	 */
	@Input() public readOnly: boolean = false;

	/**
	 * Whether we wish to show all information or only the minimal amount. By default this is 'all'
	 */
	@Input() public view: 'minimal' | 'all' = 'all';
}
